"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { dmsApi } from '@/lib/api';

interface User {
  id: string;
  name: string;
  email: string;
  departmentId: string;
  roleId?: string;
  role?: {
    id: string;
    name: string;
    permissions: string[];
  };
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (credentials: any, nextPath?: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  const getHydratedUser = async (fallbackUser?: User | null): Promise<User | null> => {
    try {
      const userData = await dmsApi.auth.me();
      return userData;
    } catch {
      return fallbackUser ?? null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('dms_token');
      if (token) {
        try {
          const userData = await getHydratedUser();
          setUser(userData);
        } catch (err) {
          localStorage.removeItem('dms_token');
          localStorage.removeItem('dms_user');
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const login = async (credentials: any, nextPath: string = '/') => {
    const { token, user: userData } = await dmsApi.auth.login(credentials);
    localStorage.setItem('dms_token', token);
    localStorage.setItem('dms_user', JSON.stringify(userData));
    const hydratedUser = await getHydratedUser(userData);
    setUser(hydratedUser);
    const dest =
      typeof nextPath === 'string' && nextPath.startsWith('/') && !nextPath.startsWith('//')
        ? nextPath
        : '/';
    router.replace(dest);
    router.refresh();
  };

  const logout = () => {
    localStorage.removeItem('dms_token');
    localStorage.removeItem('dms_user');
    setUser(null);
    router.push('/login');
  };

  const hasPermission = (permission: string) => {
    if (!user) return false;
    if (user.role?.name === 'super_admin') return true;
    const perms = user.permissions ?? user.role?.permissions;
    return Array.isArray(perms) && perms.includes(permission);
  };

  const isSuperAdmin = user?.role?.name === 'super_admin';

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout, hasPermission, isSuperAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
